import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  UsersIcon,
  KeyIcon,
  PuzzlePieceIcon,
  AdjustmentsHorizontalIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { useCanManageCurrentOrg, useGetCurrentOrg } from 'src/hooks/abilityHooks';
import { useEffect, useState } from 'react';
import HoverTooltip from './HoverTooltip';
import ConnectionStatusIconPortal from './ConnectionStatusIconPortal';
import { useConnectionStatusPortal } from 'src/hooks/useConnectionStatusPortal';
import { twMerge } from 'tailwind-merge';

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
}

interface SidebarItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

interface SidebarItemButtonProps {
  item: SidebarItem;
  isActive: boolean;
  isOpen: boolean;
  connectionStatusPortalTargetId: string;
}
interface SidebarItemContentProps {
  item: SidebarItem;
  isActive: boolean;
  isOpen: boolean;
  connectionStatusPortalTargetId: string;
}

const Sidebar = ({ isOpen, toggleSidebar }: SidebarProps) => {
  const pathname = window.location.pathname;
  const organization = useGetCurrentOrg();
  const orgId = organization?.id || '';
  const [ showTooltip, setShowTooltip ] = useState<string | null>(null);
  const isAdmin = useCanManageCurrentOrg();
  const connectionStatusPortalTargetId = 'connection-status-icon-portal';
  const { connectionStatus, updateConnectionStatus } = useConnectionStatusPortal();
  const isConnected = connectionStatus ?? false;

  useEffect(() => {
    if (!orgId) { return; }

    updateConnectionStatus(orgId);
  }, [orgId, updateConnectionStatus]);

  const adminMenuItems: SidebarItem[] = (organization && isAdmin) ? [
    {
      name: 'General Settings',
      path: `/${orgId}/general-settings`,
      icon: (
        <AdjustmentsHorizontalIcon
          className={`w-6 h-6 ${!isConnected && !isOpen ? 'text-red-500' : ''}`}
          onMouseEnter={() => {
            if (!isConnected && !isOpen) {
              setShowTooltip("Organization Not Connected");
            }
          }}
          onMouseLeave={() => {
            if (!isConnected && !isOpen) {
              setShowTooltip(null);
            }
          }}
        />
      ),
    },
    {
      name: 'Dashboard Config',
      path: `/${orgId}/dashboard-config`,
      icon: <Cog6ToothIcon className="w-6 h-6" />
    },
    {
      name: 'Billing & Subscriptions',
      path: `/${orgId}/billing`,
      icon: <CreditCardIcon className="w-6 h-6" />
    },
    {
      name: 'User Management',
      path: `/${orgId}/users`,
      icon: <UsersIcon className="w-6 h-6" />
    },
    {
      name: 'API Keys',
      path: `/${orgId}/api-keys`,
      icon: <KeyIcon className="w-6 h-6" />
    },
    {
      name: 'Specialties',
      path: `/${orgId}/specialties`,
      icon: <PuzzlePieceIcon className="w-6 h-6" />
    },
    {
      name: 'HL7 Logs',
      path: `/${orgId}/hl7-logs`,
      icon: <ClipboardDocumentListIcon className="w-6 h-6" />
    }
  ] : [];

  useEffect(() => {
    setShowTooltip(null);
  }, [isOpen]);

  useEffect(() => {
    const closeOnResize = () => {
      if (window.innerWidth < 768 && isOpen) {
        toggleSidebar();
      }
    };
    window.addEventListener('resize', closeOnResize);
    return () => window.removeEventListener('resize', closeOnResize);
  }, [isOpen, toggleSidebar]);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden transition-opacity"
          onClick={toggleSidebar}
        />
      )}
    <div className={`fixed h-screen bg-white border-r border-gray-200 transition-all duration-300 flex flex-col z-40 ${isOpen ? 'w-64' : 'w-16'}`}>
      <div className="p-4 flex items-center justify-between">
        {isOpen && (
          <div className="flex items-center text-black font-bold text-2xl">
            <img src="/Patient360.svg" alt="Patient360" className="w-24" />
          </div>
        )}
        <button
          onClick={toggleSidebar}
          className="text-gray-500 hover:bg-gray-200 p-1 rounded-full ml-auto"
        >
          {isOpen ? <ChevronLeftIcon className="w-5 h-5" /> : <ChevronRightIcon className="w-5 h-5" />}
        </button>
      </div>

      {showTooltip && <HoverTooltip text={showTooltip} />}

      {isAdmin && organization && (
        <ConnectionStatusIconPortal
          portalTargetId={connectionStatusPortalTargetId}
          onTooltipChange={setShowTooltip}
          isOpen={isOpen}
        />
      )}

      <div className="flex-1 overflow-y-auto mt-4">
        <nav className={`${isOpen ? 'pl-2' : 'pl-0'} space-y-1`}>
          {isAdmin && organization && (
            <>
              {adminMenuItems.map((item) => {
                return (
                  <SidebarItemButton
                    key={item.name}
                    item={item}
                    isActive={pathname === item.path}
                    isOpen={isOpen}
                    connectionStatusPortalTargetId={connectionStatusPortalTargetId}
                  />
                );
              })}
            </>
          )}
        </nav>
      </div>
    </div>
    </>
  );
};

const SidebarItemButton = ({ item, isActive, isOpen, connectionStatusPortalTargetId }: SidebarItemButtonProps) => {
  const currentItemSelected = isActive ? "text-brand-600 bg-brand-50 font-medium" : "text-gray-700";
  const itemDisabled = item.disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100 hover:text-brand-500";

  return (
    <div key={item.name} className="relative">
      {isActive && <div className="absolute right-0 top-0 bottom-0 w-2 bg-brand-500"></div>}
      <button
        onClick={() => { window.location.href = item.path; }}
        className={twMerge(`flex items-center w-full py-2 px-3 rounded-none transition-colors`, currentItemSelected, itemDisabled)}
        disabled={item.disabled}
      >
        <SidebarItemContent item={item} isOpen={isOpen} isActive={isActive} connectionStatusPortalTargetId={connectionStatusPortalTargetId} />
      </button>
    </div>
  );
};

const SidebarItemContent = ({ item, isOpen, isActive, connectionStatusPortalTargetId }: SidebarItemContentProps) => {
  const iconClass = `flex-shrink-0 ${isActive ? 'text-brand-600' : 'text-gray-700'}`;

  if (!isOpen) {
    return (<span className={iconClass}>{item.icon}</span>);
  }

  return (
    <>
      <span className={iconClass}>
        {item.icon}
      </span>
      <span className="ml-3 text-sm font-medium">{item.name}</span>
      {item.name === 'General Settings' && (<div id={connectionStatusPortalTargetId} className="ml-auto"></div>)}
    </>
  );
};

export default Sidebar;
